import React, { useState, useMemo } from 'react';
import { ChevronLeft, ClipboardList, X } from 'lucide-react';
import { AssistanceSectionBanner, normalizeSearchText } from './AbnormalProcedurePanel';

/**
 * MelPanel - "Minimum Equipment List (MEL)" section inside the In-Flight
 * Assistance panel (added 2026-10-16). Banner -> filterable, chapter-
 * grouped list of 121 items -> detail view (dispatch or no-go layout).
 */

const KIND_CHIP = {
  dispatch: { label: 'CAN FLY', color: '#34D399' },
  nogo: { label: 'NO-GO', color: '#F87171' },
};

const KindChip = ({ kind }) => {
  const cfg = KIND_CHIP[kind];
  if (!cfg) return null;
  return (
    <span
      className="font-plex-mono text-[10px] font-semibold px-2 py-0.5 rounded-gc-badge border uppercase tracking-wider shrink-0 w-fit"
      style={{ color: cfg.color, borderColor: cfg.color }}
      data-testid="mel-item-kind-chip"
    >
      {cfg.label}
    </span>
  );
};

export const MelBanner = ({ onClick }) => (
  <AssistanceSectionBanner
    icon={ClipboardList}
    accentColor="#4FD1F5"
    title="Minimum Equipment List (MEL)"
    subtitle="Can you still fly? 121 faults, dispatch rules and no-gos"
    onClick={onClick}
    testId="mel-banner"
  />
);

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'dispatch', label: 'Can fly' },
  { key: 'nogo', label: 'No-go' },
];

export const MelList = ({ items, onSelect, onBack, searchQuery }) => {
  const [activeFilter, setActiveFilter] = useState('all');
  const query = normalizeSearchText(searchQuery);

  const grouped = useMemo(() => {
    const sorted = [...(items || [])].sort((a, b) => a.order - b.order);
    let filtered = activeFilter === 'all' ? sorted : sorted.filter((i) => i.kind === activeFilter);
    if (query) {
      filtered = filtered.filter(
        (i) => normalizeSearchText(i.title).includes(query) || normalizeSearchText(i.search).includes(query)
      );
    }
    const groups = [];
    let current = null;
    filtered.forEach((item) => {
      if (!current || current.chapter !== item.chapter) {
        current = { chapter: item.chapter, rows: [] };
        groups.push(current);
      }
      current.rows.push(item);
    });
    return groups;
  }, [items, activeFilter, query]);

  return (
    <div data-testid="mel-list">
      <button
        onClick={onBack}
        className="flex items-center gap-1.5 mb-3 font-plex-mono text-xs w-fit"
        style={{ color: 'var(--gc-cyan)' }}
        data-testid="mel-back-btn"
      >
        <ChevronLeft className="w-3.5 h-3.5" /> BACK
      </button>

      <div className="flex gap-1.5 mb-3">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setActiveFilter(f.key)}
            className="font-plex-mono text-[10px] px-2.5 py-1 rounded-gc-badge border uppercase tracking-wider transition-colors"
            style={
              activeFilter === f.key
                ? { background: 'var(--gc-cyan)', borderColor: 'var(--gc-cyan)', color: 'var(--gc-cyan-text-on)' }
                : { borderColor: '#263042', color: 'var(--gc-text-muted)' }
            }
            data-testid={`mel-filter-${f.key}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="-mx-1 px-1">
        {grouped.length === 0 ? (
          <div className="text-center py-8 text-sm" style={{ color: 'var(--gc-text-faint)' }} data-testid="mel-no-results">
            No MEL items match "{searchQuery}"
          </div>
        ) : grouped.map((group) => (
          <div key={group.chapter} className="mb-3">
            <div
              className="font-plex-mono text-[10px] font-semibold uppercase tracking-wider mb-1.5 pb-1 border-b"
              style={{ color: 'var(--gc-text-faint)', borderColor: 'var(--gc-border-row)' }}
            >
              {group.chapter}
            </div>
            <div className="space-y-1">
              {group.rows.map((item) => (
                <button
                  key={item.id}
                  onClick={() => onSelect(item)}
                  className="w-full p-3 text-left rounded-gc-card transition-colors hover:opacity-90"
                  style={{ background: 'var(--gc-card)' }}
                  data-testid={`mel-row-${item.id}`}
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <p className="font-barlow-condensed font-bold text-[15px]" style={{ color: 'var(--gc-text-main)' }}>
                      {item.title}
                    </p>
                    <KindChip kind={item.kind} />
                  </div>
                  {item.kind === 'dispatch' && (
                    <p className="font-plex-mono text-[11px]" style={{ color: 'var(--gc-text-muted)' }}>
                      MEL {item.mmel} · Cat {item.category} · {item.repair}
                    </p>
                  )}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const MelDetail = ({ item, onClose }) => {
  if (!item) return null;
  const chip = KIND_CHIP[item.kind];

  return (
    <div
      className="fixed inset-y-0 right-0 w-full md:w-[450px] lg:w-[500px] border-l shadow-2xl z-50 overflow-y-auto"
      style={{ background: 'var(--gc-bg)', borderColor: 'var(--gc-border-nav)' }}
      data-testid="mel-detail-panel"
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-gc-button z-10"
        style={{ color: 'var(--gc-text-muted)' }}
        data-testid="close-mel-detail"
      >
        <X className="w-5 h-5" />
      </button>

      <div className="p-6 md:p-8 flex flex-col gap-5">
        <div className="flex flex-col gap-2 pb-4 border-b" style={{ borderColor: 'var(--gc-border-row)' }}>
          <span
            className="font-plex-mono text-[10px] font-semibold px-2 py-0.5 rounded-gc-badge border uppercase tracking-wider w-fit"
            style={{ color: chip.color, borderColor: chip.color }}
          >
            {chip.label}
          </span>
          <h2 className="font-barlow-condensed font-bold text-2xl" style={{ color: 'var(--gc-text-main)' }}>
            {item.title}
          </h2>
          {item.kind === 'dispatch' && (
            <p className="font-plex-mono text-xs" style={{ color: 'var(--gc-text-muted)' }}>
              MEL {item.mmel} · Category {item.category} · {item.repair}
            </p>
          )}
        </div>

        {item.kind === 'dispatch' ? (
          <>
            <div className="flex flex-col gap-2">
              <span className="font-plex-mono text-[11px] font-semibold tracking-wider" style={{ color: 'var(--gc-cyan)' }}>
                YOU CAN FLY IF:
              </span>
              <ul className="flex flex-col gap-1.5 list-disc pl-5">
                {(item.conditions || []).map((c, i) => (
                  <li key={i} className="text-sm leading-relaxed" style={{ color: 'var(--gc-text-body)' }}>{c}</li>
                ))}
              </ul>
            </div>

            {item.not_allowed && (
              <div className="px-3.5 py-2.5 rounded-gc-card border" style={{ borderColor: '#F5B041' }}>
                <p className="text-sm leading-relaxed" style={{ color: 'var(--gc-text-body)' }}>{item.not_allowed}</p>
              </div>
            )}

            {item.note && (
              <div className="px-3.5 py-2.5 rounded-gc-card" style={{ border: '1.5px dashed var(--gc-border-outline)' }}>
                <p className="text-sm leading-relaxed" style={{ color: 'var(--gc-text-body)' }}>{item.note}</p>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="px-3.5 py-2.5 rounded-gc-card border" style={{ borderColor: '#F87171' }}>
              <p className="text-sm leading-relaxed" style={{ color: 'var(--gc-text-body)' }}>{item.note}</p>
            </div>
            <p className="text-sm font-bold" style={{ color: 'var(--gc-text-main)' }}>Fix it before flight.</p>
          </>
        )}

        <p className="text-[11px] leading-relaxed pt-3 border-t" style={{ color: 'var(--gc-text-faint)', borderColor: 'var(--gc-border-row)' }}>
          Simulation use only. Based on the FAA A320 MMEL Rev 32 (30 Jul 2025), written for the Fenix IAE A320.
        </p>
      </div>
    </div>
  );
};
